import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { FittingDetail, FittingItem } from '../api/fittings';
import { formatIsk } from '../api/killboard';
import { C, FONT, frameSx, hexToRgba, lcarsLabelSx } from '../theme/tokens';

/**
 * A fit rendered the way EVE's own fitting window renders one: slot rows
 * with real item icons, one column top to bottom. Ported from the sibling
 * `poorBattlegrounds` project's `FittingWorkbench`, rebuilt in this app's
 * flat-hairline vocabulary rather than carrying its CSS across.
 *
 * The point of showing empty slots rather than only what is fitted: "three
 * empty mids" is the single most useful thing to see about a doctrine fit
 * you are about to copy, and a list of fitted modules cannot express it.
 */

// EVE's own slot-flag scheme — the same numbers routes/fittings.ts assigns
// when it parses EFT.
const SLOT_RANGES = {
  high: (f: number) => f >= 27 && f <= 34,
  mid: (f: number) => f >= 19 && f <= 26,
  low: (f: number) => f >= 11 && f <= 18,
  rig: (f: number) => f >= 92 && f <= 94,
} as const;

const DRONE_FLAG = 87;
const CHARGE_FLAG = 5;

const itemIcon = (typeId: number | null, size = 32) =>
  typeId ? `https://images.evetech.net/types/${typeId}/icon?size=${size}` : null;

/** Collapse identical modules into one entry with a count, then re-expand
 * to one row per slot — EVE shows a slot per module, not "x4". */
function groupItems(items: FittingItem[]) {
  const map = new Map<string, { typeId: number | null; name: string; qty: number }>();
  for (const it of items) {
    const key = it.type_id ? `id:${it.type_id}` : `name:${it.name}`;
    const found = map.get(key);
    if (found) found.qty += it.quantity || 1;
    else map.set(key, { typeId: it.type_id || null, name: it.name, qty: it.quantity || 1 });
  }
  return [...map.values()];
}

const SlotRow: React.FC<{ typeId: number | null; name: string; accent: string }> = ({ typeId, name, accent }) => (
  <Tooltip title={name} placement="right">
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 1,
        py: 0.5,
        borderLeft: `2px solid ${hexToRgba(accent, 0.55)}`,
        backgroundColor: hexToRgba(C.textBright, 0.02),
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 22,
          height: 22,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {typeId ? (
          <Box
            component="img"
            src={itemIcon(typeId)!}
            alt=""
            loading="lazy"
            sx={{ width: 22, height: 22 }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.style.visibility = 'hidden';
            }}
          />
        ) : (
          <Box sx={{ width: 6, height: 6, border: `1px solid ${C.hairline}` }} />
        )}
      </Box>
      <Typography
        noWrap
        sx={{ fontFamily: FONT.mono, fontSize: '0.72rem', color: C.text, minWidth: 0 }}
      >
        {name}
      </Typography>
    </Stack>
  </Tooltip>
);

const EmptySlot: React.FC = () => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    sx={{ px: 1, py: 0.5, borderLeft: `2px solid ${C.hairline}` }}
  >
    <Box sx={{ width: 22, display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: 6, height: 6, border: `1px solid ${C.hairline}` }} />
    </Box>
    <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.72rem', color: hexToRgba(C.neutral, 0.7) }}>
      empty
    </Typography>
  </Stack>
);

const SlotSection: React.FC<{ label: string; items: FittingItem[]; count: number; accent: string }> = ({
  label,
  items,
  count,
  accent,
}) => {
  const grouped = groupItems(items);
  const filled = grouped.reduce((n, g) => n + g.qty, 0);
  const empty = Math.max(0, count - filled);
  if (count === 0 && filled === 0) return null;

  return (
    <Box>
      <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.64rem'), mb: 0.5 }}>
        {label} <Box component="span" sx={{ color: hexToRgba(C.neutral, 0.8) }}>{filled}/{count}</Box>
      </Typography>
      <Stack spacing={0.25}>
        {grouped.flatMap((g) =>
          Array.from({ length: g.qty }, (_, i) => (
            <SlotRow key={`${g.typeId ?? g.name}-${i}`} typeId={g.typeId} name={g.name} accent={accent} />
          )),
        )}
        {Array.from({ length: empty }, (_, i) => (
          <EmptySlot key={`empty-${i}`} />
        ))}
      </Stack>
    </Box>
  );
};

const BaySection: React.FC<{ label: string; items: FittingItem[] }> = ({ label, items }) => {
  if (!items.length) return null;
  const grouped = groupItems(items);
  return (
    <Box>
      <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.64rem'), mb: 0.5 }}>{label}</Typography>
      <Stack spacing={0.25}>
        {grouped.map((g) => (
          <Stack key={g.typeId ?? g.name} direction="row" alignItems="center" spacing={1}>
            {g.typeId && (
              <Box component="img" src={itemIcon(g.typeId, 32)!} alt="" loading="lazy" sx={{ width: 18, height: 18 }} />
            )}
            <Typography noWrap sx={{ fontFamily: FONT.mono, fontSize: '0.7rem', color: C.text, flex: 1, minWidth: 0 }}>
              {g.name}
            </Typography>
            {g.qty > 1 && (
              <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.7rem', color: C.textDim }}>&times;{g.qty}</Typography>
            )}
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

/**
 * Slots, then cargo, one column top to bottom — High/Mid/Low/Rigs/Drones/
 * Charges, the same order EVE's own fitting window reads in. The hull render
 * used to sit beside this as a second column; it now sits in the header
 * strip next to the Jita cost (see FittingOverviewBox), which is what frees
 * this to be one column instead of fighting a wide render for width.
 */
const FittingWorkbench: React.FC<{ fitting: FittingDetail }> = ({ fitting }) => {
  const { items, stats } = fitting;

  const high = items.filter((i) => SLOT_RANGES.high(i.flag));
  const mid = items.filter((i) => SLOT_RANGES.mid(i.flag));
  const low = items.filter((i) => SLOT_RANGES.low(i.flag));
  const rig = items.filter((i) => SLOT_RANGES.rig(i.flag));
  const drones = items.filter((i) => i.flag === DRONE_FLAG);
  const charges = items.filter((i) => i.flag === CHARGE_FLAG);

  const slots = stats?.slots;

  return (
    <Stack spacing={1.5} sx={{ minWidth: 0 }}>
      <SlotSection label="High" items={high} count={slots?.high ?? high.length} accent={C.red} />
      <SlotSection label="Mid" items={mid} count={slots?.mid ?? mid.length} accent={C.cyan} />
      <SlotSection label="Low" items={low} count={slots?.low ?? low.length} accent={C.gold} />
      <SlotSection label="Rigs" items={rig} count={slots?.rig ?? rig.length} accent={C.blue} />
      <BaySection label="Drones" items={drones} />
      <BaySection label="Charges" items={charges} />
    </Stack>
  );
};

/**
 * The header strip: the hull, left, beside the one money number everyone
 * asks for first. Cost used to live in a third grid column next to the slot
 * list, which buried it in the middle of module detail and left an empty
 * track on any fit whose price failed to resolve; the hull render lived in
 * a second column beside the slots, competing with them for width. Neither
 * belongs down in the fit detail — both are "what is this, what does it
 * cost", the two facts you want before reading a single module.
 */
export const FittingOverviewBox: React.FC<{ shipTypeId: number | null; shipName: string; isk: FittingDetail['isk'] }> = ({
  shipTypeId,
  shipName,
  isk,
}) => (
  <Box sx={{ ...frameSx(C.blue), p: 1.5 }}>
    <Stack direction="row" spacing={2} alignItems="center">
      {shipTypeId && (
        <Box
          component="img"
          src={`https://images.evetech.net/types/${shipTypeId}/render?size=128`}
          alt={shipName}
          loading="lazy"
          sx={{ width: 96, height: 96, flexShrink: 0, border: `1px solid ${C.hairline}` }}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.85rem', color: C.textDim, mb: 0.75 }} noWrap>
          {shipName}
        </Typography>
        {isk ? (
          <>
            <Stack direction="row" alignItems="baseline" spacing={2} flexWrap="wrap" useFlexGap sx={{ rowGap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.62rem'), mb: 0.25 }}>Jita cost</Typography>
                <Typography sx={{ fontFamily: FONT.mono, fontSize: '1.4rem', color: C.text, lineHeight: 1.1 }}>
                  {formatIsk(isk.total)}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.75} flexWrap="wrap" useFlexGap sx={{ flex: 1, minWidth: 0 }}>
                <IskCell label="Hull" value={isk.ship} />
                <IskCell label="Modules" value={isk.modules} />
                <IskCell label="Drones" value={isk.drones} />
                <IskCell label="Ammo" value={isk.ammo} />
              </Stack>
            </Stack>
            <Typography sx={{ color: hexToRgba(C.neutral, 0.85), fontFamily: FONT.mono, fontSize: '0.62rem', mt: 1 }}>
              {isk.incomplete
                ? 'At least one item had no Jita order — this is a floor, not a quote.'
                : 'Jita 4-4, 5th-percentile sell.'}
            </Typography>
          </>
        ) : (
          <Typography sx={{ color: C.textDim, fontSize: '0.8rem' }}>No Jita price available for this fit.</Typography>
        )}
      </Box>
    </Stack>
  </Box>
);

/** One component of the total, stacked value-over-label — reads as a
 * breakdown beside the headline rather than a second list underneath it. */
const IskCell: React.FC<{ label: string; value: number }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.82rem', color: C.text, lineHeight: 1.2 }}>
        {formatIsk(value)}
      </Typography>
      <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.58rem') }}>{label}</Typography>
    </Box>
  );
};

export default FittingWorkbench;
